import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useUser } from "@/contexts/UserContext";
import { useToast } from "@/hooks/use-toast";

export const usePolicyAcknowledgement = () => {
  const { user } = useUser();
  const { toast } = useToast();
  const queryClient = useQueryClient();

  // Fetch user profile
  const { data: profile } = useQuery({
    queryKey: ["profile", user?.id],
    queryFn: async () => {
      if (!user?.id) return null;
      // @ts-ignore - admin_bubble_id exists but not in types yet
      const { data } = await supabase
        .from("profiles")
        .select("admin_bubble_id")
        .eq("id", user.id)
        .single();
      return data;
    },
    enabled: !!user?.id,
  });

  // Fetch company policies
  // @ts-ignore - admin_bubble_id and company_policies not in types yet
  const { data: companyPolicies = [], isLoading: policiesLoading } = useQuery({
    // @ts-ignore
    queryKey: ["company-policies", profile?.admin_bubble_id],
    queryFn: async () => {
      // @ts-ignore
      if (!profile?.admin_bubble_id) return [];
      
      // @ts-ignore - New table not in types yet
      const { data, error } = await supabase
        // @ts-ignore
        .from("company_policies")
        .select("*")
        // @ts-ignore
        .eq("admin_bubble_id", profile.admin_bubble_id)
        .eq("is_active", true)
        .order("policy_type");

      if (error) throw error;
      return data || [];
    },
    // @ts-ignore
    enabled: !!profile?.admin_bubble_id,
  });

  // Fetch user's acknowledgements
  // @ts-ignore - policy_acknowledgements not in types yet
  const { data: acknowledgements = [], isLoading: acknowledgementsLoading } = useQuery({
    // @ts-ignore
    queryKey: ["my-policy-acknowledgements", user?.id],
    queryFn: async () => {
      if (!user?.id) return [];
      
      console.log("🔍 [usePolicyAcknowledgement] Fetching acknowledgements for user:", user.id);
      
      // @ts-ignore - New table not in types yet
      const { data, error } = await supabase
        // @ts-ignore
        .from("policy_acknowledgements")
        .select("*")
        .eq("user_id", user.id);

      if (error) {
        console.error("❌ [usePolicyAcknowledgement] Error fetching acknowledgements:", error);
        throw error;
      }
      
      console.log("✅ [usePolicyAcknowledgement] Acknowledgements data:", data);
      return data || [];
    },
    enabled: !!user?.id,
  });

  // Check if user needs to acknowledge policies
  const pendingPolicies = companyPolicies.filter(
    (policy: any) => !acknowledgements.some((ack: any) => ack.policy_id === policy.id)
  );

  const needsAcknowledgement = pendingPolicies.length > 0;

  // Mutation to acknowledge policies
  const acknowledgePoliciesMutation = useMutation({
    mutationFn: async (policyIds: string[]) => {
      if (!user?.id) throw new Error("User not authenticated");

      const acknowledgements = policyIds.map((policy_id) => ({
        user_id: user.id,
        policy_id,
        ip_address: null, // Could be captured if needed
        user_agent: navigator.userAgent,
      }));

      // @ts-ignore - New table not in types yet
      const { error } = await supabase
        // @ts-ignore
        .from("policy_acknowledgements")
        .insert(acknowledgements);

      if (error) throw error;
    },
    onSuccess: () => {
      console.log("✅ [usePolicyAcknowledgement] Policies acknowledged, invalidating cache");
      queryClient.invalidateQueries({ queryKey: ["my-policy-acknowledgements"] });
      toast({
        title: "Success",
        description: "Policies acknowledged successfully",
      });
    },
    onError: (error) => {
      toast({
        title: "Error",
        description: error.message,
        variant: "destructive",
      });
    },
  });

  return {
    companyPolicies,
    pendingPolicies,
    needsAcknowledgement,
    isLoading: policiesLoading || acknowledgementsLoading,
    acknowledgePolicies: acknowledgePoliciesMutation.mutate,
    isAcknowledging: acknowledgePoliciesMutation.isPending,
  };
};
